import React, { useState, useEffect } from 'react';
import { FileUpload } from 'primereact/fileupload';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Divider } from 'primereact/divider';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';

import './styles.scss';

const ImageEnhancer = () => {
    const [file, setFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [imageDimensions, setImageDimensions] = useState(null);
    const [proportions, setProportions] = useState(['']);
    const [loading, setLoading] = useState(false);
    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
    const [loadingDuration, setLoadingDuration] = useState(0);
    const toast = React.useRef(null);

    const loadingMessages = [
        'Iniciando o processamento...',
        'Analisando a imagem...',
        'Ajustando proporções...',
        'Aplicando melhorias...',
        'Finalizando e preparando o download...'
    ];

    const handleFileSelect = (e) => {
        const selectedFile = e.files[0];
        setFile(selectedFile);

        // Gerar visualização da imagem
        const reader = new FileReader();
        reader.onload = (event) => {
            setImagePreview(event.target.result);

            const img = new Image();
            img.onload = () => {
                setImageDimensions({ width: img.width, height: img.height });
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(selectedFile);
    };

    const handleProportionChange = (value, index) => {
        const updatedProportions = [...proportions];
        let numericValue = value.replace(/[^\d\-]/g, '');
        if (numericValue) {
            numericValue = Math.max(-100, Math.min(100, parseInt(numericValue, 10)));
        }
        updatedProportions[index] = numericValue ? `${numericValue}%` : '';
        setProportions(updatedProportions);
    };

    const addProportion = () => {
        if (proportions.length < 5) {
            setProportions([...proportions, '']);
        } else {
            toast.current.show({ severity: 'warn', summary: 'Limite alcançado', detail: 'Máximo de 5 proporções permitido', life: 3000 });
        }
    };

    const removeProportion = (index) => {
        const updatedProportions = proportions.filter((_, i) => i !== index);
        setProportions(updatedProportions);
    };

    const calculateDimensions = (width, height, percentage) => {
        const factor = 1 + percentage / 100;
        return {
            width: Math.round(width * factor),
            height: Math.round(height * factor)
        };
    };

    const uploadFile = async () => {
        if (!file) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: 'Selecione uma imagem antes de enviar', life: 3000 });
            return;
        }

        const validProportions = proportions.filter((p) => p.trim() !== '');
        if (validProportions.length === 0) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: 'Adicione pelo menos uma proporção válida', life: 3000 });
            return;
        }

        const formData = new FormData();
        formData.append('image', file);
        formData.append(
            'payload',
            JSON.stringify(validProportions.map((p) => ({ proportion: p })))
        );

        setLoading(true);
        setLoadingMessageIndex(0);

        const fileSizeMB = file.size / (1024 * 1024); // Tamanho em MB
        const numProportions = validProportions.length;
        const durationPerStep = Math.min(Math.max(fileSizeMB * 2, 2), 8); // Entre 2 e 8 segundos por etapa
        setLoadingDuration(durationPerStep * loadingMessages.length);

        try {
            const response = await fetch('http://54.208.53.142:5005/upload', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`Erro: ${response.statusText}`);
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = downloadUrl;
            a.download = 'processed_images.zip';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(downloadUrl);

            toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Arquivo baixado com sucesso!', life: 3000 });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: error.message, life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const resetAll = () => {
        setFile(null);
        setImagePreview(null);
        setImageDimensions(null);
        setProportions(['']);
        setLoading(false);
        setLoadingMessageIndex(0);
    };

    return (
        <div className="p-4">
            <Toast ref={toast} />
            <h2>Enriquecimento de Imagem</h2>
            <Divider />
            <div className="p-field">
                <label htmlFor="imageUpload">Selecione a imagem</label>
                <FileUpload
                    name="image"
                    accept="image/*"
                    mode="basic"
                    customUpload
                    auto={false}
                    onSelect={handleFileSelect}
                    chooseLabel="Escolher"
                />
                {file && (
                    <>
                        <Tag className="p-mt-2" severity="info" value={file.name} />
                        {imagePreview && (
                            <img
                                src={imagePreview}
                                alt="Visualização"
                                style={{ maxWidth: '100%', maxHeight: '200px', marginTop: '10px', borderRadius: '8px' }}
                            />
                        )}
                        {imageDimensions && (
                            <div className="p-mt-4">
                                <h4>Dimensões Calculadas</h4>
                                <table className="p-datatable p-datatable-striped">
                                    <thead>
                                        <tr>
                                            <th>Proporção</th>
                                            <th>Largura</th>
                                            <th>Altura</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Atual</td>
                                            <td>{imageDimensions.width}px</td>
                                            <td>{imageDimensions.height}px</td>
                                        </tr>
                                        {[10, 15, 25, 30, 50, 75, 100].map((percent) => {
                                            const dims = calculateDimensions(imageDimensions.width, imageDimensions.height, percent);
                                            return (
                                                <tr key={percent}>
                                                    <td>{percent}% maior</td>
                                                    <td>{dims.width}px</td>
                                                    <td>{dims.height}px</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>
            <Divider />
            <h3>Proporções</h3>
            <div className="p-mt-3">
                <table className="p-datatable p-datatable-striped">
                    <thead>
                        <tr>
                            <th># </th>
                            <th>Proporção</th>
                            <th>Ação</th>
                        </tr>
                    </thead>
                    <tbody>
                        {proportions.map((proportion, index) => (
                            <tr key={index}>
                                <td>{index + 1}</td>
                                <td>
                                    <InputText
                                        placeholder="Ex.: 10% ou -10%"
                                        value={proportion}
                                        onChange={(e) =>
                                            handleProportionChange(e.target.value, index)
                                        }
                                    />
                                </td>
                                <td>
                                    <Button
                                        icon="pi pi-trash"
                                        className="p-button-danger"
                                        onClick={() => removeProportion(index)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <Button
                    label="Adicionar Proporção"
                    icon="pi pi-plus"
                    className="p-mt-3"
                    onClick={addProportion}
                />
            </div>
            <Divider />
            <div className="p-mt-3" style={{ display: 'flex', gap: '10px' }}>
                <Button
                    label="Limpar Tudo"
                    icon="pi pi-times"
                    className="p-button-danger"
                    onClick={resetAll}
                />
                {!loading ? (
                    <Button
                        label="Enviar"
                        icon="pi pi-upload"
                        className="p-button-success"
                        onClick={uploadFile}
                    />
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexDirection: 'column' }}>
                        <ProgressSpinner />
                        <span>{loadingMessages[loadingMessageIndex]}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageEnhancer;
